import React, {Children, isValidElement, useState} from 'react';
import PropTypes from 'prop-types';
import {ListSubheader, TextField} from '@mui/material';

const textOf = (node) => {
    if (node == null || typeof node === 'boolean') {
        return '';
    }

    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }

    if (Array.isArray(node)) {
        return node.map(textOf).join(' ');
    }

    return isValidElement(node) ? textOf(node.props.children) : '';
};

const normalise = (text) => text.toLowerCase().replace(/\s+/g, '');

/**
 * Maria, 8 October 2026: the Medicine and Brand name dropdowns on the Medical tab stay as they are, with a
 * search box at the top of the open list that finds the item. An item matches on its words, or on the extra
 * words in its data-search, such as a medicine's brand names. The chosen item is always kept in the list.
 */
const SearchableSelectField = ({children, value, SelectProps, searchTestId, ...rest}) => {
    const [query, setQuery] = useState('');
    const wanted = normalise(query);

    const items = Children.toArray(children).filter(isValidElement).map(child => {
        const words = `${textOf(child.props.children)} ${child.props['data-search'] ?? ''}`;
        const shown = !wanted || normalise(words).includes(wanted);
        const isChosen = child.props.value === value;

        return shown || isChosen ? (shown ? child : React.cloneElement(child, {sx: {display: 'none'}})) : null;
    });

    return (
        <TextField
            select
            value={value}
            SelectProps={{
                ...SelectProps,
                MenuProps: {autoFocus: false, ...(SelectProps?.MenuProps ?? {})},
                onClose: (event) => {
                    setQuery('');
                    SelectProps?.onClose?.(event);
                }
            }}
            {...rest}
        >
            <ListSubheader sx={{pt: 1, pb: 1}}>
                <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Search"
                    value={query}
                    inputProps={{'aria-label': 'Search the list', 'data-testid': searchTestId}}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        // Typed letters stay in the search box rather than jumping through the list.
                        if (event.key !== 'Escape') {
                            event.stopPropagation();
                        }
                    }}
                />
            </ListSubheader>
            {items}
        </TextField>
    );
};

SearchableSelectField.propTypes = {
    children: PropTypes.node,
    value: PropTypes.any,
    SelectProps: PropTypes.object,
    searchTestId: PropTypes.string
};

export default SearchableSelectField;
